// Città con il PRG nei gpkg di OmniSurv (OmniSurv/data/gis), servite dal
// backend legacy /api/gpkg/{city}/* (stesse risposte delle API OmniSurv, che
// però accettano solo l'abbonamento OmniSurv completo):
//  - "pro" (Ancona, Castelfidardo, …): token dell'account OmniSurv con
//    abbonamento OmniSurv o SurvMaps;
//  - gratuite (kind "free" + gpkg: Senigallia, Osimo "nuova", …): senza login.
// Niente normativa: solo i nomi delle zone (per Osimo, chi vuole il PDF della
// normativa sceglie la versione "Legacy" nel picker, che resta su OsimoMap).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { Button, Check, Dock, DockSep, Field, IconButton, Panel, Swatch, Tabs, formatLatLng } from "../components/ui";
import LegendPanel from "../components/LegendPanel";
import DocPanel from "../components/DocPanel";
import { BottomBar, FootLinks } from "../components/MapBits";
import { useToast } from "../components/Toast";
import useDraw from "../hooks/useDraw";
import useMeasure from "../hooks/useMeasure";
import { BACKEND } from "../lib/config";
import { getJson, postForDownload, postJson } from "../lib/http";
import { authHeaders } from "../lib/omnisurv";
import { addGoogleBasemap, reticleIcon } from "../lib/basemap";
import { bindPattern, ensurePattern, leafletStyleFromStile, outerRing } from "../lib/zones";

const MIN_ZONE_ZOOM = 15;  // sotto, Ancona restituisce decine di migliaia di poligoni
const ZONE_LIMIT = 5000;   // oltre: non si disegna un piano a metà, si chiede di avvicinarsi
const SELECTION_STYLE = { color: "#84e34c", weight: 3, fillColor: "#84e34c", fillOpacity: 0.08 };

export default function GpkgMap({ city, token, onMap, onAuthError, docs }) {
  const toast = useToast();
  const headers = useMemo(() => authHeaders(token), [token]);
  const legacyBase = `${BACKEND}/api/gpkg/${encodeURIComponent(city.key)}`;
  const api = useCallback((path, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${legacyBase}/${path}${qs ? `?${qs}` : ""}`;
  }, [legacyBase]);
  const elRef = useRef(null);
  const [map, setMap] = useState(null);
  const rendererRef = useRef(null);
  const zonesLayerRef = useRef(null);
  const selectionRef = useRef(null);
  const markerRef = useRef(null);
  const catastoRef = useRef(null);
  const fcRef = useRef(null);
  const abortRef = useRef(null);
  const querySeq = useRef(0);

  const [legenda, setLegenda] = useState(null);
  const [visible, setVisible] = useState(() => new Set());
  const [opacity, setOpacity] = useState(0.45);
  const [catastoOn, setCatastoOn] = useState(false);
  const [zoneState, setZoneState] = useState("idle"); // idle | loading | zoom | capped | ok | error
  const [panel, setPanel] = useState(null);           // legend | parcel | dxf
  const [info, setInfo] = useState(null);
  const [parcel, setParcel] = useState(null);         // particella selezionata (per DXF)

  const [foglio, setFoglio] = useState("");
  const [particella, setParticella] = useState("");
  const [parcelError, setParcelError] = useState("");
  const [parcelBusy, setParcelBusy] = useState(false);

  const [dxfSource, setDxfSource] = useState("area");
  const [dxfOnlyVisible, setDxfOnlyVisible] = useState(true);
  const [dxfCatasto, setDxfCatasto] = useState(false);
  const [dxfBusy, setDxfBusy] = useState(false);

  const handleError = useCallback((e, fallback) => {
    if (e?.name === "AbortError") return;
    if (e?.status === 401 || e?.status === 402) { onAuthError(e.status); return; }
    toast.error(e?.status && e.status < 500 && e.message ? e.message : fallback);
  }, [onAuthError, toast]);

  // ── Stili per tabella (dalla legenda QGIS) ───────────────────────────────
  const styles = useMemo(() => {
    const out = new Map();
    legenda?.gruppi.forEach((g) => g.layer.forEach((l) => out.set(l.tabella, leafletStyleFromStile(l.stile, l.tabella, opacity))));
    return out;
  }, [legenda, opacity]);
  const stylesRef = useRef(styles);
  stylesRef.current = styles;
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  const queryArea = useRef(null);
  const draw = useDraw(map, { onCreated: (f) => queryArea.current?.(f) });
  const measure = useMeasure(map);
  const busyRef = useRef(false);
  busyRef.current = !!draw.mode || measure.active;

  // ── Mappa ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const m = L.map(elRef.current, { zoomControl: false, minZoom: 11, maxZoom: 21 });
    if (city.bbox) {
      const [x0, y0, x1, y1] = city.bbox;
      const b = L.latLngBounds([y0, x0], [y1, x1]);
      m.fitBounds(b);
      m.setMaxBounds(b.pad(0.4));
    } else {
      m.setView([city.center.lat, city.center.lng], 14);
    }
    L.control.zoom({ position: "bottomright" }).addTo(m);
    addGoogleBasemap(m);

    m.createPane("sm-zones").style.zIndex = 410;
    const catastoPane = m.createPane("sm-catasto");
    catastoPane.style.zIndex = 420;
    catastoPane.style.pointerEvents = "none";
    m.createPane("sm-selection").style.zIndex = 430;

    const renderer = L.svg({ pane: "sm-zones", padding: 0.3 });
    m.addLayer(renderer);
    rendererRef.current = renderer;
    zonesLayerRef.current = L.geoJSON(null, {
      renderer,
      pane: "sm-zones",
      style: (f) => stylesRef.current.get(f.properties.zona)?.style || { color: "#9aa0ad", weight: 1, fillOpacity: 0.2 },
      onEachFeature: (f, l) => {
        const s = stylesRef.current.get(f.properties.zona);
        if (s?.pattern) bindPattern(l, ensurePattern(renderer, s.pattern));
        l.bindTooltip(f.properties.zona, { sticky: true });
      },
    }).addTo(m);
    selectionRef.current = L.geoJSON(null, { pane: "sm-selection", style: SELECTION_STYLE, interactive: false }).addTo(m);
    catastoRef.current = L.tileLayer.wms(`${BACKEND}/api/catasto`, {
      layers: "CP.CadastralZoning,CP.CadastralParcel,fabbricati,simbolo_graffa,vestizioni",
      format: "image/png", transparent: true, version: "1.1.1", opacity: 0.9,
      minZoom: 15, maxZoom: 21, pane: "sm-catasto", attribution: "© Agenzia delle Entrate",
    });

    setMap(m);
    onMap(m, city.bbox ? L.latLngBounds([city.bbox[1], city.bbox[0]], [city.bbox[3], city.bbox[2]]).pad(0.05) : null);
    return () => {
      abortRef.current?.abort();
      onMap(null);
      setMap(null);
      m.remove();
    };
  }, [city.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Legenda ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    getJson(api("zoning/legenda"), { headers })
      .then((leg) => {
        if (cancelled) return;
        setLegenda(leg);
        // Accesi come nel progetto QGIS: layer acceso in un gruppo visibile.
        setVisible(new Set(leg.gruppi
          .filter((g) => g.visibile !== false)
          .flatMap((g) => g.layer.filter((l) => l.acceso).map((l) => l.tabella))));
      })
      .catch((e) => !cancelled && handleError(e, "Legenda del PRG non disponibile."));
    return () => { cancelled = true; };
  }, [api, headers]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Zone nell'area visibile ──────────────────────────────────────────────
  const renderZones = useCallback(() => {
    const layer = zonesLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const fc = fcRef.current;
    if (!fc) return;
    const vis = visibleRef.current;
    layer.addData({ type: "FeatureCollection", features: fc.features.filter((f) => vis.has(f.properties.zona)) });
  }, []);

  const loadZones = useCallback(async () => {
    if (!map || !legenda) return;
    abortRef.current?.abort();
    if (map.getZoom() < MIN_ZONE_ZOOM) {
      fcRef.current = null;
      renderZones();
      setZoneState("zoom");
      return;
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const b = map.getBounds().pad(0.15);
    const url = api("zoning/in-bbox", {
      minx: b.getWest(), miny: b.getSouth(), maxx: b.getEast(), maxy: b.getNorth(),
      limit: ZONE_LIMIT, gruppi: legenda.gruppi.map((g) => g.id).join(","),
    });
    setZoneState("loading");
    try {
      const fc = await getJson(url, { headers, signal: ctrl.signal });
      if (fc.capped) { setZoneState("capped"); return; } // resta il disegno precedente
      fcRef.current = fc;
      renderZones();
      setZoneState("ok");
    } catch (e) {
      if (e.name === "AbortError") return;
      setZoneState("error");
      handleError(e, "Zone del PRG non disponibili.");
    }
  }, [map, legenda, api, headers, renderZones, handleError]);

  useEffect(() => {
    if (!map || !legenda) return undefined;
    let t = null;
    const onMove = () => { clearTimeout(t); t = setTimeout(loadZones, 250); };
    map.on("moveend", onMove);
    loadZones();
    return () => { clearTimeout(t); map.off("moveend", onMove); };
  }, [map, legenda, loadZones]);

  // Cambio di zone visibili o di opacità: si ridisegna senza ricaricare.
  useEffect(() => { renderZones(); }, [visible, styles, renderZones]);

  useEffect(() => {
    const m = map;
    const layer = catastoRef.current;
    if (!m || !layer) return;
    if (catastoOn) layer.addTo(m); else m.removeLayer(layer);
  }, [map, catastoOn]);

  // ── Interrogazioni ───────────────────────────────────────────────────────
  const setMarker = (latlng) => {
    if (markerRef.current) map.removeLayer(markerRef.current);
    markerRef.current = latlng ? L.marker(latlng, { icon: reticleIcon, interactive: false }).addTo(map) : null;
  };

  const showSelection = (geometry) => {
    selectionRef.current?.clearLayers();
    if (geometry) selectionRef.current?.addData(geometry);
  };

  const zonesIn = async (geometry) => {
    const ring = outerRing(geometry);
    if (!ring) return [];
    const res = await postJson(api("zoning/zones-in-aoi"), { polygon: ring }, { headers });
    return res.zones || [];
  };

  const queryPoint = useCallback(async (latlng) => {
    const seq = ++querySeq.current;
    setMarker(latlng);
    setInfo({ kind: "point", latlng, loading: true });
    const pt = { lat: latlng.lat, lng: latlng.lng };
    try {
      const [z, p] = await Promise.all([
        getJson(api("zoning/zones-at", pt), { headers }),
        getJson(api("catasto/at-point", pt), { headers }).catch((e) => {
          if (e.status === 401 || e.status === 402) throw e;
          return null;
        }),
      ]);
      if (seq !== querySeq.current) return;
      setInfo({ kind: "point", latlng, zones: z.zones || [], parcel: p?.found ? p : null });
    } catch (e) {
      if (seq !== querySeq.current) return;
      setInfo(null);
      handleError(e, "Interrogazione del punto non riuscita.");
    }
  }, [map, api, headers, handleError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!map) return undefined;
    const onClick = (e) => { if (!busyRef.current) queryPoint(e.latlng); };
    map.on("click", onClick);
    return () => map.off("click", onClick);
  }, [map, queryPoint]);

  const selectParcel = async (p, { fit } = {}) => {
    const seq = ++querySeq.current;
    setParcel({ foglio: p.foglio, particella: p.particella, geometry: p.geometry });
    setDxfSource("parcel");
    showSelection(p.geometry);
    if (fit && p.bbox) map.fitBounds([[p.bbox[1], p.bbox[0]], [p.bbox[3], p.bbox[2]]], { padding: [80, 80], maxZoom: 19 });
    setInfo({ kind: "parcel", parcel: p, loading: true });
    try {
      const zones = await zonesIn(p.geometry);
      if (seq === querySeq.current) setInfo({ kind: "parcel", parcel: p, zones });
    } catch (e) {
      if (seq === querySeq.current) setInfo({ kind: "parcel", parcel: p, zones: null });
      handleError(e, "Zone della particella non disponibili.");
    }
  };

  const searchParcel = async () => {
    const f = foglio.trim();
    const p = particella.trim();
    if (!f || !p) { setParcelError("Inserisci foglio e particella."); return; }
    setParcelError("");
    setParcelBusy(true);
    try {
      const data = await getJson(api("catasto/search", { foglio: f, particella: p }), { headers });
      setMarker(null);
      await selectParcel(data, { fit: true });
    } catch (e) {
      if (e.status === 404) setParcelError(`Particella non trovata: foglio ${f}, particella ${p}.`);
      else handleError(e, "Ricerca della particella non riuscita.");
    } finally {
      setParcelBusy(false);
    }
  };

  queryArea.current = async (feature) => {
    const seq = ++querySeq.current;
    setMarker(null);
    setDxfSource("area");
    setInfo({ kind: "area", loading: true });
    try {
      const zones = await zonesIn(feature.geometry);
      if (seq === querySeq.current) setInfo({ kind: "area", zones });
    } catch (e) {
      if (seq === querySeq.current) setInfo({ kind: "area", zones: null });
      handleError(e, "Zone dell'area non disponibili.");
    }
  };

  // ── DXF ──────────────────────────────────────────────────────────────────
  const exportDxf = async () => {
    const geometry = dxfSource === "area" ? draw.shape?.geometry : parcel?.geometry;
    if (!geometry) {
      toast.info(dxfSource === "area" ? "Disegna prima l'area da esportare." : "Seleziona prima una particella.");
      return;
    }
    if (dxfOnlyVisible && visible.size === 0 && !dxfCatasto) {
      toast.info("Nessuna zona visibile in legenda: accendine almeno una o includi il catasto.");
      return;
    }
    setDxfBusy(true);
    try {
      await postForDownload(
        `${legacyBase}/export_dxf`,
        { geometry, layers: dxfOnlyVisible ? [...visible] : null, include_catasto: dxfCatasto },
        `${city.key}_export.dxf`,
        { headers, fallbackError: "Esportazione DXF non riuscita." },
      );
      toast.ok("DXF scaricato.");
    } catch (e) {
      handleError(e, "Esportazione DXF non riuscita.");
    } finally {
      setDxfBusy(false);
    }
  };

  // ── Legenda: dati per il pannello ────────────────────────────────────────
  const legendGroups = useMemo(() => (legenda?.gruppi || []).map((g) => ({
    id: g.id,
    name: g.nome,
    visible: g.layer.some((l) => visible.has(l.tabella)),
    items: g.layer.map((l) => ({
      id: l.tabella, label: l.titolo, swatch: styles.get(l.tabella)?.swatch, visible: visible.has(l.tabella),
    })),
  })), [legenda, visible, styles]);

  const setTables = (tables, on) => setVisible((s) => {
    const n = new Set(s);
    tables.forEach((t) => (on ? n.add(t) : n.delete(t)));
    return n;
  });
  const toggleGroup = (id) => {
    const g = legendGroups.find((x) => x.id === id);
    if (g) setTables(g.items.map((i) => i.id), !g.visible);
  };
  const toggleItem = (_gid, table) => setTables([table], !visible.has(table));
  const setItems = (gid, on, only) => {
    const g = legendGroups.find((x) => x.id === gid);
    if (g) setTables((only || g.items).map((i) => i.id), on);
  };

  // ── Strumenti ────────────────────────────────────────────────────────────
  const togglePanel = (name) => setPanel((p) => (p === name ? null : name));
  const startDraw = (kind) => { measure.stop(); draw.start(kind); };
  const toggleMeasure = () => {
    if (measure.active) { measure.stop(); return; }
    draw.cancel();
    measure.start();
  };
  const clearAll = () => {
    draw.clear();
    showSelection(null);
    setParcel(null);
    setMarker(null);
    setInfo(null);
  };

  const hint =
    zoneState === "zoom" ? "Avvicinati per vedere le zone del PRG"
    : zoneState === "capped" ? "Area molto densa: avvicinati per aggiornare le zone"
    : zoneState === "loading" ? "Caricamento zone…"
    : zoneState === "error" ? "Zone del PRG non disponibili"
    : "Clicca sulla mappa per vedere zone e particella";

  const infoTitle = !info ? ""
    : info.kind === "point" ? "Punto"
    : info.kind === "parcel" ? `Particella ${info.parcel.foglio}/${info.parcel.particella}`
    : "Area disegnata";

  return (
    <>
      <div ref={elRef} className="sm-map" />

      <Dock>
        <IconButton icon="layers" label="Legenda" active={panel === "legend"} onClick={() => togglePanel("legend")} />
        <IconButton icon="grid" label="Cerca particella" active={panel === "parcel"} onClick={() => togglePanel("parcel")} />
        <DockSep />
        <IconButton icon="polygon" label="Disegna un poligono" active={draw.mode === "polygon"} onClick={() => startDraw("polygon")} />
        <IconButton icon="square" label="Disegna un rettangolo" active={draw.mode === "rectangle"} onClick={() => startDraw("rectangle")} />
        <IconButton icon="trash" label="Cancella disegno e selezione" disabled={!draw.shape && !parcel && !info} onClick={clearAll} />
        <DockSep />
        <IconButton icon="download" label="Esporta DXF" active={panel === "dxf"} onClick={() => togglePanel("dxf")} />
        {docs?.available && (
          <IconButton icon="file-text" label="Documento intervento" active={panel === "docs"} onClick={() => togglePanel("docs")} />
        )}
        <IconButton icon="ruler" label="Misura distanze" active={measure.active} onClick={toggleMeasure} />
      </Dock>

      {panel === "docs" && (
        <DocPanel city={city} shape={draw.shape} auth={docs.auth} onDraw={startDraw} onClose={() => setPanel(null)} />
      )}

      {panel === "legend" && (
        <LegendPanel
          title={`Legenda · ${city.name}`}
          groups={legendGroups}
          onToggleGroup={toggleGroup}
          onToggleItem={toggleItem}
          onSetItems={setItems}
          filterable
          defaultExpanded={legendGroups.length === 1}
          onClose={() => setPanel(null)}
        >
          <Check checked={catastoOn} onChange={() => setCatastoOn((v) => !v)}>
            Catasto (Agenzia delle Entrate, da zoom 15)
          </Check>
          <Field label={`Opacità zone · ${Math.round(opacity * 100)}%`}>
            <input className="sm-range" type="range" min="0.1" max="0.9" step="0.05" value={opacity}
              onChange={(e) => setOpacity(parseFloat(e.target.value))} />
          </Field>
        </LegendPanel>
      )}

      {panel === "parcel" && (
        <Panel
          title="Cerca particella"
          onClose={() => setPanel(null)}
          footer={<Button block icon="search" busy={parcelBusy} onClick={searchParcel}>Cerca</Button>}
        >
          <div className="sm-field-row">
            <Field label="Foglio" htmlFor="pro-foglio">
              <input id="pro-foglio" className="sm-input sm-input--mono" value={foglio} placeholder="es. 10B"
                onChange={(e) => setFoglio(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchParcel()} />
            </Field>
            <Field label="Particella" htmlFor="pro-particella">
              <input id="pro-particella" className="sm-input sm-input--mono" value={particella} placeholder="es. 187"
                onChange={(e) => setParticella(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchParcel()} />
            </Field>
          </div>
          {parcelError && <p className="sm-panel-error">{parcelError}</p>}
        </Panel>
      )}

      {panel === "dxf" && (
        <Panel
          title="Esporta DXF"
          onClose={() => setPanel(null)}
          footer={<Button block icon="download" busy={dxfBusy} onClick={exportDxf}>Esporta DXF</Button>}
        >
          <Tabs
            value={dxfSource}
            onChange={setDxfSource}
            options={[
              { value: "area", label: "Area disegnata", disabled: !draw.shape },
              { value: "parcel", label: "Particella", disabled: !parcel },
            ]}
          />
          <p className="sm-panel-text">
            {dxfSource === "area"
              ? (draw.shape ? "Zone ritagliate sull'area disegnata." : "Disegna un'area con gli strumenti a sinistra.")
              : (parcel ? `Zone ritagliate sulla particella ${parcel.foglio}/${parcel.particella}.` : "Cerca o clicca una particella e selezionala.")}
          </p>
          <Check checked={dxfOnlyVisible} onChange={() => setDxfOnlyVisible((v) => !v)}>
            Solo le zone accese in legenda ({visible.size})
          </Check>
          <Check checked={dxfCatasto} onChange={() => setDxfCatasto((v) => !v)}>
            Includi le particelle catastali (un layer per particella)
          </Check>
          <p className="sm-panel-text sm-muted">Coordinate UTM 33N (WGS84), area massima 2 km².</p>
        </Panel>
      )}

      {info && (
        <Panel
          side="right"
          title={infoTitle}
          onClose={() => { setInfo(null); setMarker(null); }}
          footer={info.kind !== "point" && (
            <Button variant="secondary" size="sm" icon="download" onClick={() => setPanel("dxf")}>Esporta DXF</Button>
          )}
        >
          {info.kind === "point" && <p className="sm-panel-text sm-mono">{formatLatLng(info.latlng)}</p>}
          {info.kind === "point" && !info.loading && (
            info.parcel ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <dl className="sm-kv" style={{ flex: 1 }}>
                  <dt>Foglio</dt><dd>{info.parcel.foglio}</dd>
                  <dt>Particella</dt><dd>{info.parcel.particella}</dd>
                </dl>
                <Button variant="secondary" size="sm" onClick={() => selectParcel(info.parcel)}>Seleziona</Button>
              </div>
            ) : <p className="sm-panel-text sm-muted">Nessuna particella catastale in questo punto.</p>
          )}

          <h3 className="sm-section-title">Zone PRG</h3>
          {info.loading ? (
            <p className="sm-panel-text"><span className="sm-spinner" style={{ display: "inline-block", verticalAlign: "middle", marginRight: 8 }} />Interrogazione in corso…</p>
          ) : info.zones == null ? (
            <p className="sm-panel-error">Zone non disponibili.</p>
          ) : info.zones.length === 0 ? (
            <p className="sm-panel-text sm-muted">Nessuna zona del PRG.</p>
          ) : (
            <ul className="sm-zone-list">
              {info.zones.map((z) => (
                <li key={z}><Swatch swatch={styles.get(z)?.swatch} />{z}</li>
              ))}
            </ul>
          )}
          <p className="sm-panel-text sm-muted">
            Sono i nomi delle zone del PRG vigente; per le norme consulta le NTA del Comune.
          </p>
        </Panel>
      )}

      <BottomBar measure={measure} draw={draw} idleHint={hint}>
        <span className="sm-label">Area disegnata</span>
        <Button size="sm" icon="download" onClick={() => { setDxfSource("area"); setPanel("dxf"); }}>Esporta DXF</Button>
        {docs?.available && (
          <Button variant="secondary" size="sm" icon="file-text" onClick={() => setPanel("docs")}>Documento</Button>
        )}
        <Button variant="ghost" size="sm" icon="trash" onClick={draw.clear}>Cancella</Button>
      </BottomBar>

      <FootLinks />
    </>
  );
}
